import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Trash2, Download, FileText, Image as ImageIcon, Film, Music, HardDrive } from "lucide-react";
import FileUpload from "@/components/FileUpload";

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function iconFor(ct) {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  if (ct.startsWith("audio/")) return Music;
  return FileText;
}

export default function Files() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/files");
      setRows(data.files || []);
      setTotal(data.total || 0);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/files/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("File deleted.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "image") return r.content_type?.startsWith("image/");
    if (filter === "pdf")   return r.content_type === "application/pdf";
    if (filter === "video") return r.content_type?.startsWith("video/");
    if (filter === "audio") return r.content_type?.startsWith("audio/");
    return r.purpose === filter;
  });

  const purposes = Array.from(new Set(rows.map((r) => r.purpose).filter(Boolean)));
  const totalBytes = rows.reduce((s, r) => s + (r.size || 0), 0);

  return (
    <div className="p-8 max-w-7xl" data-testid="files-page">
      <Toaster position="top-right" richColors />

      <div className="mb-8">
        <div className="overline mb-2 flex items-center gap-2"><HardDrive className="w-3.5 h-3.5" /> File storage</div>
        <h1 className="text-3xl font-bold text-neutral-900">Files & media</h1>
        <p className="text-neutral-600 mt-2 max-w-3xl text-sm">
          Every upload is stored in Emergent object storage and mirrored in MongoDB.
          Use for client documents, business photos, audit PDFs, uploaded logos, and generated media.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Files" value={total} />
        <Kpi label="Total size" value={fmtBytes(totalBytes)} />
        <Kpi label="Purposes" value={purposes.length} />
        <Kpi label="Latest" value={rows[0] ? fmtDateTime(rows[0].created_at) : "—"} />
      </div>

      <div className="grid md:grid-cols-[300px_1fr] gap-6">
        <div>
          <FileUpload
            purpose="general"
            onUploaded={(r) => setRows((prev) => [{ ...r }, ...prev])}
            label="Upload a new file"
          />
          <div className="mt-4 text-xs text-neutral-500 space-y-1">
            <div><strong>Allowed:</strong> images, PDFs, docs, small video/audio.</div>
            <div><strong>Max size:</strong> 25 MB per file.</div>
          </div>
        </div>

        <div>
          <div className="flex gap-2 mb-3 flex-wrap">
            {["all", "image", "pdf", "video", "audio", ...purposes].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`files-filter-${k}`}
                className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border ${filter === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-300 hover:border-neutral-900"}`}
              >
                {k}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-neutral-500 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-neutral-300 bg-white p-12 text-center text-neutral-500">
              <HardDrive className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
              <div className="font-display text-lg mb-1">No files yet</div>
              <div className="text-sm">Drop a file on the left to see it appear here.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filtered.map((r) => <FileCard key={r.id} row={r} onDelete={() => remove(r.id)} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-neutral-200 bg-white p-4">
      <div className="text-xs font-mono uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5 truncate">{value}</div>
    </div>
  );
}

function FileCard({ row, onDelete }) {
  const Icon = iconFor(row.content_type);
  const isImage = row.content_type?.startsWith("image/");
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!isImage) return;
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${row.id}/download`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        setBlobUrl(u);
        revoke = u;
      } catch { /* noop */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [row.id, isImage]);

  const download = async () => {
    try {
      const resp = await api.get(`/files/${row.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = row.original_filename;
      a.click();
      URL.revokeObjectURL(u);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="border border-neutral-200 bg-white flex flex-col" data-testid={`file-card-${row.id}`}>
      <div className="aspect-square bg-neutral-50 flex items-center justify-center overflow-hidden">
        {isImage && blobUrl ? (
          <img src={blobUrl} alt={row.original_filename} className="w-full h-full object-cover" />
        ) : (
          <Icon className="w-10 h-10 text-neutral-400" />
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-xs font-mono text-neutral-500 uppercase truncate">{row.purpose || "general"}</div>
        <div className="text-sm font-semibold text-neutral-900 truncate mt-0.5" title={row.original_filename}>
          {row.original_filename}
        </div>
        <div className="text-xs text-neutral-400 mt-1">{fmtBytes(row.size)} · {fmtDateTime(row.created_at)}</div>
        <div className="mt-auto pt-3 flex gap-1">
          <button onClick={download} className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1" data-testid={`file-download-${row.id}`}>
            <Download className="w-3 h-3" /> Download
          </button>
          <button onClick={onDelete} className="text-xs text-neutral-400 hover:text-rose-600 inline-flex items-center gap-1 ml-auto" data-testid={`file-delete-${row.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
